import {
  queryOptions,
  useMutation,
  useQueries,
  useQueryClient,
} from '@tanstack/react-query';
import { useMemo, useState } from 'react';
import { toast } from 'sonner';
import {
  fetchAdminCompanyProfile,
  patchAdminCompanyAnswer,
} from '@/api/(admin)/companies';
import type {
  DataPointSearchResult,
  ProfileTemplate,
} from '@/api/network/types';
import type {
  GeneralInfoSearchSection,
  GeneralInfoTemplateId,
  ProfileSearchState,
} from '@/app/(client)/network/-types';
import { buildGeneralInfoSearchSections } from '@/app/(client)/network/-utils/generalInfo';
import { serializeFieldAnswer } from '@/app/(client)/network/-utils/serializeFieldAnswer';
import { useTranslator } from '@/hooks/useTranslator';
import { visibleProfileDataPoints } from '../-utils/profileDataPoints';

const TEMPLATE_IDS: GeneralInfoTemplateId[] = [
  'ABOUT',
  'OTHER_IDS',
  'OPERATIONAL_METRICS',
];

/** One template's profile. Shared by the tab's route loader and the hook. */
export function adminCompanyProfileQueryOptions(
  companyId: string,
  templateId: GeneralInfoTemplateId,
) {
  return queryOptions({
    queryKey: ['admin', 'companies', 'profile', companyId, templateId] as const,
    queryFn: () => fetchAdminCompanyProfile(companyId, templateId),
    enabled: Boolean(companyId),
    staleTime: 60_000,
  });
}

/** The three the tab reads, in the order it shows them. */
export const GENERAL_INFO_TEMPLATE_IDS = TEMPLATE_IDS;

const EMPTY_DRAFT: Record<GeneralInfoTemplateId, Record<string, unknown>> = {
  ABOUT: {},
  OTHER_IDS: {},
  OPERATIONAL_METRICS: {},
};

/** Stable empty set — keeps the sidebar sections memo from thrashing. */
const NO_MATCHED_IDS = new Set<GeneralInfoTemplateId>();

/**
 * The three profile templates behind the General Information tab, read and
 * written through the admin endpoints.
 *
 * The customer tab's renderer is reused as-is; only the data source differs.
 * `/admin/companies/:id/profile/:template` is unscoped, which is the whole point
 * — an operator opens companies that are in nobody's network — and the matching
 * write is `/admin/companies/templates/:template/answer`.
 */
export function useAdminGeneralInfo(companyId: string) {
  const { getTranslation: t } = useTranslator({ page: 'managementCompanies' });
  // The renderer's own copy lives in `companyDetail` — the section titles it
  // shows have to come from the same namespace it is handed.
  const { getTranslation: tProfile } = useTranslator({ page: 'companyDetail' });
  const queryClient = useQueryClient();

  const results = useQueries({
    queries: TEMPLATE_IDS.map(templateId =>
      adminCompanyProfileQueryOptions(companyId, templateId),
    ),
  });

  /**
   * Narrowed to the data points the old profile actually listed.
   *
   * Filtered here rather than in the renderer: the section badge and the
   * completion sidebar both count `template.data`, so dropping the rows without
   * dropping them from the data would leave the counts saying 37 while 26 are
   * on screen.
   */
  const templates = useMemo<
    Record<GeneralInfoTemplateId, ProfileTemplate | undefined>
  >(() => {
    const narrow = (
      template: ProfileTemplate | undefined,
    ): ProfileTemplate | undefined =>
      template
        ? { ...template, data: visibleProfileDataPoints(template.data) }
        : undefined;

    return {
      ABOUT: narrow(results[0]?.data?.data),
      OTHER_IDS: narrow(results[1]?.data?.data),
      OPERATIONAL_METRICS: narrow(results[2]?.data?.data),
    };
  }, [results]);

  const isLoading = results.some(r => r.isLoading);
  const isError = results.some(r => r.isError);

  const [draft, setDraft] =
    useState<Record<GeneralInfoTemplateId, Record<string, unknown>>>(
      EMPTY_DRAFT,
    );
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});
  const [search, setSearch] = useState('');

  const mutation = useMutation({
    mutationFn: ({
      templateId,
      payload,
    }: {
      templateId: GeneralInfoTemplateId;
      payload: Parameters<typeof patchAdminCompanyAnswer>[2];
    }) => patchAdminCompanyAnswer(companyId, templateId, payload),
  });

  /**
   * Saves one field and reports whether it stuck.
   *
   * The renderer keeps the edited value in `draft` until this resolves true, so
   * a refused write has to answer false — otherwise the cell would show a value
   * the server never took.
   */
  async function onFieldChange(
    templateId: GeneralInfoTemplateId,
    key: string,
    value: unknown,
  ): Promise<boolean> {
    const dataPoint = templates[templateId]?.data?.[key];
    if (!dataPoint) return false;

    const queryKey = ['admin', 'companies', 'profile', companyId, templateId];
    const previous = queryClient.getQueryData<{ data: ProfileTemplate }>(
      queryKey,
    );
    const serialized = serializeFieldAnswer(dataPoint.fieldTypeId, value);

    setDraft(prev => ({
      ...prev,
      [templateId]: { ...prev[templateId], [key]: value },
    }));

    // Optimistic, as the customer tab does it: the field shows the new value
    // while the write is in flight instead of snapping back to the old one and
    // forward again when the refetch lands.
    queryClient.setQueryData<{ data: ProfileTemplate }>(queryKey, old => {
      const existing = old?.data?.data?.[key];
      if (!old || !existing) return old;
      return {
        ...old,
        data: {
          ...old.data,
          data: {
            ...old.data.data,
            [key]: {
              ...existing,
              value: serialized.value,
              ...(serialized.unit !== undefined
                ? { unit: serialized.unit }
                : {}),
              ...(serialized.year !== undefined
                ? { year: serialized.year }
                : {}),
            },
          },
        },
      };
    });

    try {
      // The target company is the URL's `companyId` now, not a body field —
      // there is no relational copy to distinguish it from, since an operator
      // always edits the company's own profile here.
      await mutation.mutateAsync({
        templateId,
        payload: { answer: { [key]: serialized } },
      });
      setFieldErrors(prev => {
        const next = { ...prev };
        delete next[key];
        return next;
      });
      await queryClient.refetchQueries({ queryKey });
      toast.success(t('toast.saved'));
      return true;
    } catch (error) {
      // Put the server's value back — the optimistic one was never accepted.
      if (previous) queryClient.setQueryData(queryKey, previous);
      const message =
        error instanceof Error ? error.message : t('errors.saveFailed');
      setFieldErrors(prev => ({ ...prev, [key]: message }));
      toast.error(t('errors.saveFailed'), { description: message });
      setDraft(prev => {
        const next = { ...prev[templateId] };
        delete next[key];
        return { ...prev, [templateId]: next };
      });
      return false;
    }
  }

  /**
   * Field search, matched over the loaded templates rather than on the server.
   *
   * The customer tab searches through `/api/companies/:id/data-points-answers`,
   * which is network-scoped and would refuse a company the operator has no
   * relationship with — the case this page exists for. The old admin tab
   * filtered client-side for the same reason, so this matches it: same three
   * templates, already in memory.
   */
  const searchResults: DataPointSearchResult[] | undefined = useMemo(() => {
    const query = search.trim().toLowerCase();
    if (query === '') return undefined;

    const matches: DataPointSearchResult[] = [];
    for (const templateId of TEMPLATE_IDS) {
      const data = templates[templateId]?.data;
      if (!data) continue;
      for (const [key, dataPoint] of Object.entries(data)) {
        const name = dataPoint?.name ?? key;
        if (name.toLowerCase().includes(query)) {
          matches.push({ retracedId: key, templateId, name });
        }
      }
    }
    return matches;
  }, [search, templates]);

  const sectionLabels = useMemo<Record<GeneralInfoTemplateId, string>>(
    () => ({
      ABOUT: tProfile('generalInfo.aboutSection'),
      OTHER_IDS: tProfile('generalInfo.otherIdsSection'),
      OPERATIONAL_METRICS: tProfile('generalInfo.operationalMetricsSection'),
    }),
    [tProfile],
  );

  // One section per template with hits, in sidebar order — the renderer shows
  // one template at a time, so without this the matches in the other two are
  // dropped and a query matching none of the selected one blanks the pane
  // (DELTA-2461).
  const searchSections = useMemo<GeneralInfoSearchSection[]>(
    () =>
      searchResults
        ? buildGeneralInfoSearchSections(
            searchResults,
            templates,
            sectionLabels,
          )
        : [],
    [searchResults, templates, sectionLabels],
  );

  const searchState: ProfileSearchState =
    search.trim().length === 0
      ? 'inactive'
      : searchSections.length > 0
        ? 'results'
        : isLoading
          ? 'loading'
          : 'empty';

  // Only highlight the sidebar while the search actually has something to show.
  const matchedTemplateIds = useMemo(
    () =>
      searchState === 'results'
        ? new Set(searchSections.map(s => s.templateId))
        : NO_MATCHED_IDS,
    [searchState, searchSections],
  );

  return {
    templates,
    isLoading,
    isError,
    draft,
    fieldErrors,
    onFieldChange,
    search,
    setSearch,
    searchState,
    searchSections,
    matchedTemplateIds,
    sectionLabels,
  };
}
